import { createSlice } from "@reduxjs/toolkit";
import {
  getLeaseAgreementThunk,
  createLeaseAgreementThunk,
  updateLeaseAgreementThunk,
  deleteLeaseAgreementThunk,
  acceptOfferLeaseAgreementThunk,
  bulkCreateLeaseAgreementThunk,
} from "state/action/lease_agreement";

// ─────────────────────────────────────────────────────────────────────────────
// HELPERS
// ─────────────────────────────────────────────────────────────────────────────

const asyncState = (data = null) => ({
  data,
  isPending: false,
  isFulfilled: false,
  isRejected: false,
  err: null,
});

const buildAsyncCases = (builder, thunk, stateKey) => {
  builder
    .addCase(thunk.pending, (state) => {
      state[stateKey] = { ...asyncState(), isPending: true };
    })
    .addCase(thunk.fulfilled, (state, action) => {
      state[stateKey] = { ...asyncState(action.payload), isFulfilled: true };
    })
    .addCase(thunk.rejected, (state, action) => {
      state[stateKey] = {
        ...asyncState(),
        isRejected: true,
        err: action.error.message ?? "Unknown error",
      };
    });
};

// ─────────────────────────────────────────────────────────────────────────────
// INITIAL SHAPES
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Satu baris harga yang ditawarkan per komponen
 */
const buildPriceOfferRow = () => ({
  charge: "", // e.g. "Rental Fee" | "Service Charge" | ...
  currency: "IDR",
  price: 0,
  frequency: "Monthly", // "daily" | "weekly" | "monthly" | "yearly"
  measurement: "sqm", // "sqm" | "unit" | "flat"
});

/**
 * Satu unit yang sudah dipilih lengkap dengan harga per unit-nya
 */
const buildSelectedUnit = (unit = {}) => ({
  _id: unit._id || "",
  unit_number: unit.unit_number || "",
  area_sqm: unit.area_sqm || unit.size_sqm || 0,
  lease_status: unit.lease_status || "",
  // Harga custom per unit (jika base_price_source === "unit")
  price_subtotals: unit.price_subtotals || [],
});

/**
 * Satu service = satu blok lokasi + unit + harga penawaran
 */
const buildPropertyItem = () => ({
  // ── Lokasi ────────────────────────────────────────────────────────────────
  property: "", // ref: Property._id
  building: "", // ref: Building._id
  floor: "", // ref: Floor._id
  unit: [], // array of buildSelectedUnit()
  costs: [],
  financials: {
    cost_responsibility: "",
    revenue_type: null,
    details: {},
  },

  // ── Periode per service ───────────────────────────────────────────────────
  fitting_out_period: null, // Tanggal serah terima unit untuk fitting out
  grace_period: { value: 0, unit: "weeks" }, // Durasi grace period untuk fitting out
  handover_date: null,
  start_date: null,
  end_date: null,

  // ── Hierarki harga dasar ──────────────────────────────────────────────────
  // "property" | "building" | "floor" | "unit" | ""
  base_price_source: "",

  // Cache snapshot harga dari masing-masing level hierarki
  // (diisi saat user memilih property/building/floor dari API)
  base_price_snapshot: {
    property: null, // { _id, name, items: [...] }
    building: null,
    floor: null,
    unit: null,
  },

  // ── Harga yang ditawarkan (override / negosiasi) ──────────────────────────
  price_unit_offer: [], // array of buildPriceOfferRow()
});

// ─────────────────────────────────────────────────────────────────────────────
// COST HELPERS
// ─────────────────────────────────────────────────────────────────────────────

const getDetailsCost = (costType) => {
  switch (costType) {
    case "Rental Fee":
      return {
        rate_per_sqm: "",
        area_sqm: "",
        billing_period: "monthly",
        ppn_excluded: true,
        rate_periods: [
          { label: "", start_period: "", end_period: "", rate_per_sqm: "" },
        ],
        total: "",
        billing_date: "",
        due_date: "",
      };
    case "Service Charge":
      return {
        rate_per_sqm: "",
        area_sqm: "",
        billing_period: "monthly",
        subsequent_rate_policy:
          "Mengikuti kebijakan Property Management 2 (PM2)",
        total: "",
        billing_date: "",
        due_date: "",
      };

    case "Utility Electricity Charge":
      return {
        payment_schema: "per_usage",
        billing_period: "monthly",
        cost: "",
        billing_date: "",
        due_date: "",
      };
    case "Utility Water Charge":
      return {
        payment_schema: "per_usage",
        billing_period: "monthly",
        cost: "",
        billing_date: "",
        due_date: "",
      };
    case "Fit Out Charge":
      return {
        rate_per_sqm: "",
        area_sqm: "",
        total: "",
        billing_date: "",
        due_date: "",
      };
    case "Fit Out Deposit":
      return {
        total: "",
        is_refundable: true,
        refund_condition: "Dikembalikan pada saat periode sewa berakhir",
        refund_date: "",
        billing_date: "",
        due_date: "",
      };
    case "Down Payment":
      return {
        total: "",
        allocation_start_month: 1,
        allocation_months: 3,
        reference_document: "Surat Kesepakatan",
        billing_date: "",
        due_date: "",
      };
    case "Security Deposit":
      return {
        rent_months: 1,
        service_charge_months: 1,
        total: "",
        refund_condition: "Dikembalikan pada saat periode sewa berakhir",
        refund_date: "",
        billing_date: "",
        due_date: "",
      };
    case "Guarantee Fee":
      return {
        payment_method: "",
        guarantee_value: "",
        guarantee: "",
        billing_date: "",
        due_date: "",
      };
    case "Commitment Fee":
      return {
        total: "",
        billing_date: "",
        due_date: "",
      };
    default:
      return {};
  }
};

const buildCostItem = () => ({
  cost_type: "",
  description: "",
  paymentType: "periodic",
  periodic: {
    frequency: "monthly",
    daily: { hour: "08" },
    weekly: { day: "1" },
    monthly: { date: "01" },
    yearly: { date: "" },
    billing_config: {
      billing_offset_days: 0,
      due_offset_days: 14,
      revenue_recognition_offset_days: 30,
    },
  },
  nonPeriodic: null,
  details: {},
});

// ─────────────────────────────────────────────────────────────────────────────
// FINANCIALS HELPERS
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Menghasilkan struktur 'details' untuk financials berdasarkan revenue_type.
 * Identik dengan getDetailFinancials di pks.js.
 */
const getDetailFinancials = (itemType) => {
  switch (itemType) {
    case "Bagi Hasil":
      return {
        bagi_hasil_method: "flat_rate",

        flat_rate: {
          is_rate_flat: true,
          rate_percent: "",
          sales_amount: 0,
          calculated_amount: "",
        },

        mgp: {
          is_active: false,
          mgp_amount: 0,
          mgp_rate_percent: "",
          mgp_basis: "per_month",
          calculated_mgp_amount: 0,
        },

        yearly_rates: [
          {
            year: 1,
            description: "",
            rate_percent: 0,
          },
        ],

        // Metode: Parameter Target Sales (Conditional Rate)
        conditional_rate: {
          tiers: [
            {
              sales_target: 0,
              rate_percent: "",
              description: "",
            },
          ],
        },

        // Metode: Skema Nilai Berjenjang (Tiered Scheme)
        tiered_scheme: {
          tiers: [
            {
              year: 1,
              amount: 0,
              description: "",
            },
          ],
        },

        frequency: "daily", // "daily" | "weekly" | "monthly" | "quarterly" | "yearly"

        daily: { hour: "08" },
        weekly: { day: "1" },
        monthly: { date: "01" },
        quarterly: { month_offset: 1, date: "01" },
        yearly: { date: "2024-01-01" },

        billing_config: {
          billing_offset_days: 0,
          due_offset_days: 14,
          revenue_recognition_offset_days: 30,
          sales_recapitulation_days: 0,
        },
      };

    case "Bagi Hasil dengan MGP":
      return {
        scheme_type: "", // "TIERED_RATE" | "FLAT_MINIMUM_SHARE" | "PERIOD"
        payment_period: "Tahunan",
        calculated_amount: "",
        scheme: null,
      };

    case "Bukan Sewa":
      return null;

    default:
      return {};
  }
};

/**
 * Menghasilkan struktur 'scheme' berdasarkan scheme_type untuk MGP.
 * Identik dengan getSchemeRevenueShareWithMGP di pks.js.
 */
const getSchemeRevenueShareWithMGP = (itemType) => {
  switch (itemType) {
    case "PERIOD":
      return {
        rules: [
          {
            condition_description: "",
            rate_percent: 0,
            period_amount: 0,
          },
        ],
        paymentType: "periodic",
        periodic: {
          frequency: "daily",
          daily: { hour: "08" },
          billing_config: {
            billing_offset_days: 0,
            due_offset_days: 14,
            revenue_recognition_offset_days: 30,
            sales_recapitulation_days: 0,
          },
        },
      };
    default:
      return {};
  }
};

// ─────────────────────────────────────────────────────────────────────────────
// OFFER FORM INITIAL
// ─────────────────────────────────────────────────────────────────────────────

export const OFFER_FORM_INITIAL = {
  // ── Identitas Dokumen ───────────────────────────────────────────────────
  agreement_no: "",
  agreement_type: "Sewa Unit", // "Sewa Unit" | "PKS"
  transaction_type: "New Lease", // "New Lease" | "Renewal" | "Addendum"
  type: "Lease",
  status: "DRAFT",

  // ── Administrasi ────────────────────────────────────────────────────────
  working_unit: "",
  salesman_id: "",

  // ── Pihak yang Terlibat ─────────────────────────────────────────────────
  mitra: null,
  tenant_representative: "",
  landlord_representative: "",

  // ── Jadwal Penawaran ────────────────────────────────────────────────────
  offer_date: null,
  offer_period: null,
  offerEnd_date: null,
  meeting_date: null,

  // ── Masa Sewa ───────────────────────────────────────────────────────────
  start_date: null,
  end_date: null,
  total_period: "",

  // ── Term ─────────────────────────────────────────────────────────────────
  term: {
    sign_document_date: null,
    effective_date: null,
    isSameEffectiveDate: false,
  },

  // ── Services (Layanan / Unit Sewa) ──────────────────────────────────────
  properties: [buildPropertyItem()],

  // ── Task Workflow ───────────────────────────────────────────────────────
  task: {
    title: "Accept Offer / Offer Negotiate",
    status: "on progress",
    description: "Accept Offer / Offer Negotiate",
    role: ["MITRA"],
    assignee: null,
  },

  // ── Paragraf Surat ───────────────────────────────────────────────────────
  letter_opening: "",
  letter_closing_1: "",
  letter_closing_2: "",

  // ── Metadata ────────────────────────────────────────────────────────────
  note: "",
  attachments: [],

  // ── UI helper ────────────────────────────────────────────────────────────
  _expandedUnit: "",
};

export const SELECTED_AGREEMENT_INITIAL = {
  _id: null,
  agreement_no: "",
  agreement_type: "",
  transaction_type: "",
  type: "",
  status: "",
  working_unit: "",
  department: "",
  salesman_id: "",
  mitra: null,
  tenant_representative: "",
  landlord_representative: "",
  offer_date: null,
  offerEnd_date: null,
  start_date: null,
  end_date: null,
  total_period: "",
  handOver_date: null,
  fittingOut_date: null,
  followUp_date: null,
  type_handover: "",
  term: {},
  properties: [],
  task: null,
  note: "",
  attachments: [],
};

// ─────────────────────────────────────────────────────────────────────────────
// INITIAL STATE
// ─────────────────────────────────────────────────────────────────────────────

const initialState = {
  getLeaseAgreement: asyncState(),
  createLeaseAgreement: asyncState(),
  updateLeaseAgreement: asyncState(),
  deleteLeaseAgreement: asyncState(),
  acceptOfferLeaseAgreement: asyncState(),

  createOfferLeaseAgreementInputBody: { ...OFFER_FORM_INITIAL },

  createSignLeaseAgreementInputBody: {
    agreement_no: "",
    transaction_type: "",
    working_unit: "",
    type: "",
    status: "",
    salesman_id: "",
    tenant: null,
    offer_date: null,
    start_date: null,
    end_date: null,
    fittingOut_date: null,
    handOver_date: null,
    followUp_date: null,
    billing: null,
    currency: "IDR",
    deposit: "",
    paymentMethod: "",
    maximal_payment_date: "",
    charge_mode: "",
    fixed_rate: "",
    note: "",
  },

  selectedAgreement: { ...SELECTED_AGREEMENT_INITIAL },
  current_step: "",
  leaseAgreement_object_value: "",
};

// ─────────────────────────────────────────────────────────────────────────────
// SLICE
// ─────────────────────────────────────────────────────────────────────────────

export const leaseAgreementSlice = createSlice({
  name: "leaseAgreement",
  initialState,
  reducers: {
    // ── Offer form: field-level update ─────────────────────────────────────
    setOfferField: (state, action) => {
      const { field, value } = action.payload;
      state.createOfferLeaseAgreementInputBody[field] = value;
    },

    setTermField: (state, action) => {
      const { field, value } = action.payload;
      state.createOfferLeaseAgreementInputBody.term[field] = value;
    },

    setTaskField: (state, action) => {
      const { field, value } = action.payload;
      state.createOfferLeaseAgreementInputBody.task[field] = value;
    },

    // ── Services: CRUD ─────────────────────────────────────────────────────
    addServiceItem: (state) => {
      state.createOfferLeaseAgreementInputBody.properties.push(
        buildPropertyItem(),
      );
    },

    removeServiceItem: (state, action) => {
      const idx = action.payload;
      if (state.createOfferLeaseAgreementInputBody.properties.length > 1) {
        state.createOfferLeaseAgreementInputBody.properties.splice(idx, 1);
      }
    },

    updateServiceItem: (state, action) => {
      const { index, field, value } = action.payload;
      const svc = state.createOfferLeaseAgreementInputBody.properties[index];
      if (svc) svc[field] = value;
    },

    // ── Service: periode tanggal ───────────────────────────────────────────
    setServiceDate: (state, action) => {
      const { serviceIndex, field, value } = action.payload;
      const svc =
        state.createOfferLeaseAgreementInputBody.properties[serviceIndex];
      if (svc) svc[field] = value;
    },

    // ── Service: hierarki harga dasar ──────────────────────────────────────
    /**
     * Pilih sumber harga dasar: "property" | "building" | "floor" | "unit" | ""
     */
    setBasePriceSource: (state, action) => {
      const { serviceIndex, source } = action.payload;
      const svc =
        state.createOfferLeaseAgreementInputBody.properties[serviceIndex];
      if (svc) {
        svc.base_price_source = source;
        // Reset harga tawar saat sumber berubah agar tidak stale
        svc.price_unit_offer = [];
      }
    },

    /**
     * Simpan snapshot harga dari level tertentu (property/building/floor/unit)
     * dipanggil setelah API return data property/building/floor
     */
    setBasePriceSnapshot: (state, action) => {
      const { serviceIndex, level, data } = action.payload;
      // level: "property" | "building" | "floor" | "unit"
      const svc =
        state.createOfferLeaseAgreementInputBody.properties[serviceIndex];
      if (svc) {
        if (!svc.base_price_snapshot) svc.base_price_snapshot = {};
        svc.base_price_snapshot[level] = data;
      }
    },

    /**
     * Update harga per unit secara manual (untuk mode base_price_source === "unit")
     */
    updateUnitPriceSubtotal: (state, action) => {
      const { serviceIndex, unitId, priceIndex, field, value } = action.payload;
      const svc =
        state.createOfferLeaseAgreementInputBody.properties[serviceIndex];
      if (!svc) return;
      const unit = svc.unit.find((u) => u._id === unitId);
      if (!unit) return;
      if (!unit.price_subtotals) unit.price_subtotals = [];
      if (unit.price_subtotals[priceIndex]) {
        unit.price_subtotals[priceIndex][field] = value;
        // Recalculate subtotal
        if (field === "price" || field === "qty") {
          const p = unit.price_subtotals[priceIndex];
          p.subtotal = (Number(p.price) || 0) * (Number(p.qty) || 0);
        }
      }
    },

    addUnitPriceSubtotal: (state, action) => {
      const { serviceIndex, unitId } = action.payload;
      const svc =
        state.createOfferLeaseAgreementInputBody.properties[serviceIndex];
      if (!svc) return;
      const unit = svc.unit.find((u) => u._id === unitId);
      if (!unit) return;
      if (!unit.price_subtotals) unit.price_subtotals = [];
      unit.price_subtotals.push({
        key: "",
        label: "",
        price: 0,
        qty: 0,
        unit: "sqm",
        subtotal: 0,
        currency: "IDR",
        frequency: "monthly",
      });
    },

    removeUnitPriceSubtotal: (state, action) => {
      const { serviceIndex, unitId, priceIndex } = action.payload;
      const svc =
        state.createOfferLeaseAgreementInputBody.properties[serviceIndex];
      if (!svc) return;
      const unit = svc.unit.find((u) => u._id === unitId);
      if (!unit || !unit.price_subtotals) return;
      unit.price_subtotals.splice(priceIndex, 1);
    },

    // ── Service: price_unit_offer (harga negosiasi) ────────────────────────
    addPriceOfferRow: (state, action) => {
      const { serviceIndex } = action.payload;
      const svc =
        state.createOfferLeaseAgreementInputBody.properties[serviceIndex];
      if (svc) svc.price_unit_offer.push(buildPriceOfferRow());
    },

    removePriceOfferRow: (state, action) => {
      const { serviceIndex, rowIndex } = action.payload;
      const svc =
        state.createOfferLeaseAgreementInputBody.properties[serviceIndex];
      if (svc) svc.price_unit_offer.splice(rowIndex, 1);
    },

    updatePriceOfferRow: (state, action) => {
      const { serviceIndex, rowIndex, field, value } = action.payload;
      const row =
        state.createOfferLeaseAgreementInputBody.properties[serviceIndex]
          ?.price_unit_offer?.[rowIndex];
      if (row) row[field] = value;
    },

    // ── Service: costs CRUD ────────────────────────────────────────────────
    addCostItem: (state, action) => {
      const { serviceIndex } = action.payload;
      const svc =
        state.createOfferLeaseAgreementInputBody.properties[serviceIndex];
      if (!svc) return;
      if (!svc.costs) svc.costs = [];
      svc.costs.push(buildCostItem());
    },

    removeCostItem: (state, action) => {
      const { serviceIndex, costIndex } = action.payload;
      const svc =
        state.createOfferLeaseAgreementInputBody.properties[serviceIndex];
      if (svc?.costs) svc.costs.splice(costIndex, 1);
    },

    updateCostItem: (state, action) => {
      const { serviceIndex, costIndex, field, value } = action.payload;
      const cost =
        state.createOfferLeaseAgreementInputBody.properties[serviceIndex]
          ?.costs?.[costIndex];
      if (!cost) return;
      cost[field] = value;
      if (field === "cost_type") cost.details = getDetailsCost(value);
    },

    updateCostItemDetails: (state, action) => {
      const { serviceIndex, costIndex, field, value } = action.payload;
      const cost =
        state.createOfferLeaseAgreementInputBody.properties[serviceIndex]
          ?.costs?.[costIndex];
      if (cost) cost.details[field] = value;
    },

    setCostPaymentType: (state, action) => {
      const { serviceIndex, costIndex, value } = action.payload;
      const cost =
        state.createOfferLeaseAgreementInputBody.properties[serviceIndex]
          ?.costs?.[costIndex];
      if (!cost) return;
      cost.paymentType = value;
      if (value === "periodic") {
        cost.periodic = {
          frequency: "monthly",
          daily: { hour: "08" },
          weekly: { day: "1" },
          monthly: { date: "01" },
          yearly: { date: "" },
          billing_config: {
            billing_offset_days: 0,
            due_offset_days: 14,
            revenue_recognition_offset_days: 30,
          },
        };
        cost.nonPeriodic = null;
      } else {
        cost.nonPeriodic = {
          totalInstallment: 1,
          terms: [{ term: 1, date: "", percentage: 100, amount: 0, notes: "" }],
        };
        cost.periodic = null;
      }
    },

    setCostPeriodicFrequency: (state, action) => {
      const { serviceIndex, costIndex, value } = action.payload;
      const cost =
        state.createOfferLeaseAgreementInputBody.properties[serviceIndex]
          ?.costs?.[costIndex];
      if (cost?.periodic) cost.periodic.frequency = value;
    },

    setCostBillingConfig: (state, action) => {
      const { serviceIndex, costIndex, field, value } = action.payload;
      const cost =
        state.createOfferLeaseAgreementInputBody.properties[serviceIndex]
          ?.costs?.[costIndex];
      if (cost?.periodic?.billing_config)
        cost.periodic.billing_config[field] = Number(value) || 0;
    },

    addCostTerm: (state, action) => {
      const { serviceIndex, costIndex } = action.payload;
      const cost =
        state.createOfferLeaseAgreementInputBody.properties[serviceIndex]
          ?.costs?.[costIndex];
      if (!cost?.nonPeriodic) return;
      const next = cost.nonPeriodic.terms.length + 1;
      cost.nonPeriodic.terms.push({
        term: next,
        date: "",
        percentage: 0,
        amount: 0,
        notes: "",
      });
      cost.nonPeriodic.totalInstallment = next;
    },

    updateCostTerm: (state, action) => {
      const { serviceIndex, costIndex, termIndex, field, value } =
        action.payload;
      const cost =
        state.createOfferLeaseAgreementInputBody.properties[serviceIndex]
          ?.costs?.[costIndex];
      const t = cost?.nonPeriodic?.terms?.[termIndex];
      if (t) t[field] = value;
    },

    removeCostTerm: (state, action) => {
      const { serviceIndex, costIndex, termIndex } = action.payload;
      const cost =
        state.createOfferLeaseAgreementInputBody.properties[serviceIndex]
          ?.costs?.[costIndex];
      if (!cost?.nonPeriodic?.terms) return;
      cost.nonPeriodic.terms.splice(termIndex, 1);
      cost.nonPeriodic.terms.forEach((t, i) => {
        t.term = i + 1;
      });
      cost.nonPeriodic.totalInstallment = cost.nonPeriodic.terms.length;
    },

    // ── Rental Fee: rate_periods CRUD ──────────────────────────────────────
    addCostRatePeriod: (state, action) => {
      const { serviceIndex, costIndex } = action.payload;
      const cost =
        state.createOfferLeaseAgreementInputBody.properties[serviceIndex]
          ?.costs?.[costIndex];
      if (!Array.isArray(cost?.details?.rate_periods)) return;
      cost.details.rate_periods.push({
        label: "",
        start_period: "",
        end_period: "",
        rate_per_sqm: "",
      });
    },

    removeCostRatePeriod: (state, action) => {
      const { serviceIndex, costIndex, periodIndex } = action.payload;
      const cost =
        state.createOfferLeaseAgreementInputBody.properties[serviceIndex]
          ?.costs?.[costIndex];
      if (!Array.isArray(cost?.details?.rate_periods)) return;
      cost.details.rate_periods.splice(periodIndex, 1);
    },

    updateCostRatePeriod: (state, action) => {
      const { serviceIndex, costIndex, periodIndex, field, value } =
        action.payload;
      const cost =
        state.createOfferLeaseAgreementInputBody.properties[serviceIndex]
          ?.costs?.[costIndex];
      const period = cost?.details?.rate_periods?.[periodIndex];
      if (period) period[field] = value;
    },

    // ── Service: unit selection ────────────────────────────────────────────
    /**
     * Toggle memilih/batal pilih unit dalam service.
     * unitObj = object unit lengkap (bukan hanya id) agar snapshot tersimpan.
     */
    toggleUnitInService: (state, action) => {
      const { serviceIndex, unitObj, checked } = action.payload;
      const svc =
        state.createOfferLeaseAgreementInputBody.properties[serviceIndex];
      if (!svc) return;
      const id = typeof unitObj === "object" ? unitObj._id : unitObj;
      const existingIds = svc.unit.map((u) =>
        typeof u === "object" ? u._id : u,
      );
      if (checked) {
        if (!existingIds.includes(id)) {
          // Simpan full object agar area_sqm dll tersedia
          svc.unit.push(
            typeof unitObj === "object"
              ? buildSelectedUnit(unitObj)
              : buildSelectedUnit({ _id: id }),
          );
        }
      } else {
        svc.unit = svc.unit.filter(
          (u) => (typeof u === "object" ? u._id : u) !== id,
        );
      }
    },

    // ── Service: financials ────────────────────────────────────────────────
    /**
     * Set revenue_type pada financials sebuah service, sekaligus auto-populate
     * details berdasarkan tipe yang dipilih.
     * Payload: { serviceIndex, newType }
     */
    setServiceFinancialsType: (state, action) => {
      const { serviceIndex, newType } = action.payload;
      const svc =
        state.createOfferLeaseAgreementInputBody.properties[serviceIndex];
      if (!svc) return;
      if (!svc.financials)
        svc.financials = {
          cost_responsibility: "",
          revenue_type: null,
          details: {},
        };
      svc.financials.revenue_type = newType;
      svc.financials.details = getDetailFinancials(newType);
    },

    /**
     * Update field (termasuk nested path) di dalam financials sebuah service.
     * Payload: { serviceIndex, path, value }
     * Contoh path: "details.bagi_hasil_method" | "details.flat_rate.rate_percent"
     *              | "cost_responsibility"
     */
    setServiceFinancialsField: (state, action) => {
      const { serviceIndex, path, value } = action.payload;
      const svc =
        state.createOfferLeaseAgreementInputBody.properties[serviceIndex];
      if (!svc?.financials) return;

      const keys = path.split(".");
      let current = svc.financials;
      for (let i = 0; i < keys.length - 1; i++) {
        if (current[keys[i]] === undefined || current[keys[i]] === null) {
          current[keys[i]] = {};
        }
        current = current[keys[i]];
      }
      current[keys[keys.length - 1]] = value;
    },

    /**
     * Set scheme_type untuk "Bagi Hasil dengan MGP" dan auto-populate scheme.
     * Payload: { serviceIndex, newSchemeType }
     */
    setServiceFinancialsSchemeType: (state, action) => {
      const { serviceIndex, newSchemeType } = action.payload;
      const svc =
        state.createOfferLeaseAgreementInputBody.properties[serviceIndex];
      if (!svc?.financials?.details) return;
      svc.financials.details.scheme_type = newSchemeType;
      svc.financials.details.scheme =
        getSchemeRevenueShareWithMGP(newSchemeType);
    },

    /**
     * Tambah entry baru ke details.yearly_rates (untuk metode "Bagi Hasil").
     * Payload: { serviceIndex }
     */
    addServiceFinancialsYearlyRate: (state, action) => {
      const { serviceIndex } = action.payload;
      const svc =
        state.createOfferLeaseAgreementInputBody.properties[serviceIndex];
      const yearlyRates = svc?.financials?.details?.yearly_rates;
      if (!Array.isArray(yearlyRates)) return;
      yearlyRates.push({
        year: yearlyRates.length + 1,
        description: "",
        rate_percent: 0,
      });
    },

    /**
     * Hapus entry dari details.yearly_rates.
     * Payload: { serviceIndex, rateIndex }
     */
    removeServiceFinancialsYearlyRate: (state, action) => {
      const { serviceIndex, rateIndex } = action.payload;
      const svc =
        state.createOfferLeaseAgreementInputBody.properties[serviceIndex];
      const yearlyRates = svc?.financials?.details?.yearly_rates;
      if (!Array.isArray(yearlyRates)) return;
      yearlyRates.splice(rateIndex, 1);
      yearlyRates.forEach((r, i) => {
        r.year = i + 1;
      });
    },

    /**
     * Tambah entry baru ke details.scheme.rules (untuk skema MGP "PERIOD").
     * Payload: { serviceIndex }
     */
    addServiceFinancialsMgpRule: (state, action) => {
      const { serviceIndex } = action.payload;
      const svc =
        state.createOfferLeaseAgreementInputBody.properties[serviceIndex];
      const rules = svc?.financials?.details?.scheme?.rules;
      if (!Array.isArray(rules)) return;
      rules.push({
        condition_description: "",
        rate_percent: 0,
        period_amount: 0,
      });
    },

    /**
     * Hapus entry dari details.scheme.rules.
     * Payload: { serviceIndex, ruleIndex }
     */
    removeServiceFinancialsMgpRule: (state, action) => {
      const { serviceIndex, ruleIndex } = action.payload;
      const svc =
        state.createOfferLeaseAgreementInputBody.properties[serviceIndex];
      const rules = svc?.financials?.details?.scheme?.rules;
      if (!Array.isArray(rules)) return;
      rules.splice(ruleIndex, 1);
    },

    /**
     * Set cost_responsibility pada financials.
     * Payload: { serviceIndex, value }
     */
    setCostResponsibility: (state, action) => {
      const { serviceIndex, value } = action.payload;
      const svc =
        state.createOfferLeaseAgreementInputBody.properties[serviceIndex];
      if (!svc?.financials) return;
      svc.financials.cost_responsibility = value;
    },

    // ── UI helper ──────────────────────────────────────────────────────────
    toggleExpandedUnit: (state, action) => {
      const unitId = action.payload;
      const form = state.createOfferLeaseAgreementInputBody;
      form._expandedUnit = form._expandedUnit === unitId ? "" : unitId;
    },

    // ── Bulk setters ───────────────────────────────────────────────────────
    setCreateOfferLeaseAgreementInputBody: (state, action) => {
      state.createOfferLeaseAgreementInputBody = action.payload;
    },

    resetOfferForm: (state) => {
      state.createOfferLeaseAgreementInputBody = { ...OFFER_FORM_INITIAL };
    },

    // ── Sign form ──────────────────────────────────────────────────────────
    setSignField: (state, action) => {
      const { field, value } = action.payload;
      state.createSignLeaseAgreementInputBody[field] = value;
    },
    setCreateSignLeaseAgreementInputBody: (state, action) => {
      state.createSignLeaseAgreementInputBody = action.payload;
    },
    resetSignForm: (state) => {
      state.createSignLeaseAgreementInputBody =
        initialState.createSignLeaseAgreementInputBody;
    },

    // ── Selected agreement ─────────────────────────────────────────────────
    setSelectedLeaseAgreement: (state, action) => {
      state.selectedAgreement = action.payload;
    },
    clearSelectedLeaseAgreement: (state) => {
      state.selectedAgreement = { ...SELECTED_AGREEMENT_INITIAL };
    },

    // ── UI state ───────────────────────────────────────────────────────────
    setCurrentStep: (state, action) => {
      state.current_step = action.payload;
    },
    setLeaseAgreementObjectValue: (state, action) => {
      state.leaseAgreement_object_value = action.payload;
    },
    clearLeaseAgreementObjectValue: (state) => {
      state.leaseAgreement_object_value = "";
    },

    // ── Async state resetters ──────────────────────────────────────────────
    clearGetLeaseAgreement: (state) => {
      state.getLeaseAgreement = asyncState();
    },
    clearCreateLeaseAgreement: (state) => {
      state.createLeaseAgreement = asyncState();
    },
    clearUpdateLeaseAgreement: (state) => {
      state.updateLeaseAgreement = asyncState();
    },
    clearDeleteLeaseAgreement: (state) => {
      state.deleteLeaseAgreement = asyncState();
    },
    clearAllAsyncState: (state) => {
      state.getLeaseAgreement = asyncState();
      state.createLeaseAgreement = asyncState();
      state.updateLeaseAgreement = asyncState();
      state.deleteLeaseAgreement = asyncState();
      state.acceptOfferLeaseAgreement = asyncState();
    },

    // ── Backward-compat aliases ────────────────────────────────────────────
    setField: (state, action) => {
      const { field, value } = action.payload;
      state.createOfferLeaseAgreementInputBody[field] = value;
    },
    setAgreementField: (state, action) => {
      const { field, value } = action.payload;
      state.createOfferLeaseAgreementInputBody[field] = value;
    },
    addService: (state) => {
      state.createOfferLeaseAgreementInputBody.properties.push(
        buildPropertyItem(),
      );
    },
    removeService: (state, action) => {
      const { index } = action.payload;
      if (state.createOfferLeaseAgreementInputBody.properties.length > 1) {
        state.createOfferLeaseAgreementInputBody.properties.splice(index, 1);
      }
    },
    resetAgreementForm: (state) => {
      state.createOfferLeaseAgreementInputBody = { ...OFFER_FORM_INITIAL };
    },
    setClearGetLeaseAgreement: (state) => {
      state.getLeaseAgreement = asyncState();
    },
    setClearCreateLeaseAgreement: (state) => {
      state.createLeaseAgreement = asyncState();
    },
    setClearUpdateLeaseAgreement: (state) => {
      state.updateLeaseAgreement = asyncState();
    },
    setClearDeleteLeaseAgreement: (state) => {
      state.deleteLeaseAgreement = asyncState();
    },
    setClearAllLeaseAgreementState: (state) => {
      state.getLeaseAgreement = asyncState();
      state.createLeaseAgreement = asyncState();
      state.updateLeaseAgreement = asyncState();
      state.deleteLeaseAgreement = asyncState();
      state.acceptOfferLeaseAgreement = asyncState();
    },
    setGetLeaseAgreementObjectValue: (state, action) => {
      state.leaseAgreement_object_value = action.payload;
    },
    setClearGetLeaseAgreementObjectValue: (state) => {
      state.leaseAgreement_object_value = "";
    },
    setSelectedLeaseAgreementLegacy: (state, action) => {
      state.selectedAgreement = action.payload;
    },
    setClearSelectedLeaseAgreement: (state) => {
      state.selectedAgreement = { ...SELECTED_AGREEMENT_INITIAL };
    },
    setClearCreateOfferLeaseAgreementInputBody: (state) => {
      state.createOfferLeaseAgreementInputBody = { ...OFFER_FORM_INITIAL };
    },
    setClearSignLeaseAgreementInputBody: (state) => {
      state.createSignLeaseAgreementInputBody =
        initialState.createSignLeaseAgreementInputBody;
    },
  },

  extraReducers(builder) {
    buildAsyncCases(builder, getLeaseAgreementThunk, "getLeaseAgreement");
    buildAsyncCases(builder, createLeaseAgreementThunk, "createLeaseAgreement");
    buildAsyncCases(builder, updateLeaseAgreementThunk, "updateLeaseAgreement");
    buildAsyncCases(builder, deleteLeaseAgreementThunk, "deleteLeaseAgreement");
    buildAsyncCases(
      builder,
      acceptOfferLeaseAgreementThunk,
      "acceptOfferLeaseAgreement",
    );
  },
});

// ─────────────────────────────────────────────────────────────────────────────
// EXPORTS
// ─────────────────────────────────────────────────────────────────────────────

export const leaseAgreementAction = {
  ...leaseAgreementSlice.actions,
  getLeaseAgreementThunk,
  createLeaseAgreementThunk,
  updateLeaseAgreementThunk,
  deleteLeaseAgreementThunk,
  acceptOfferLeaseAgreementThunk,
  bulkCreateLeaseAgreementThunk,
};

export const selectLeaseAgreement = (state) => state.leaseAgreement;

export const selectOfferForm = (state) =>
  state.leaseAgreement.createOfferLeaseAgreementInputBody;

export const selectSignForm = (state) =>
  state.leaseAgreement.createSignLeaseAgreementInputBody;

export const selectSelectedAgreement = (state) =>
  state.leaseAgreement.selectedAgreement;

export const selectLeaseAgreementObjectValue = (state) =>
  state.leaseAgreement.leaseAgreement_object_value;

export const selectGetLeaseAgreement = (state) =>
  state.leaseAgreement.getLeaseAgreement;

export const selectCreateLeaseAgreement = (state) =>
  state.leaseAgreement.createLeaseAgreement;

export default leaseAgreementSlice.reducer;
